/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  AIGoal,
  CityStats,
  GameStatus,
  Grid,
  Milestone,
  NewsItem,
  ResearchState,
  Scenario,
} from '../types';
import type { ActiveModal } from '../components/UIOverlay';
import { useGamePersistenceApp } from './useGamePersistenceApp';
import { useEnhancedObjectivesIntegration } from './useEnhancedObjectivesIntegration';
import { useGameSessionHandlers } from './useGameSessionHandlers';

export interface UseGameSessionBundleDeps {
  newsFeed: NewsItem[];
  setNewsFeed: Dispatch<SetStateAction<NewsItem[]>>;
  currentGoal: AIGoal | null;
  setCurrentGoal: Dispatch<SetStateAction<AIGoal | null>>;
  statsDay: number;
  activeScenario: Scenario | null;
  gameStarted: boolean;
  gameStatus: GameStatus;
  stats: CityStats;
  grid: Grid;
  research: ResearchState;
  gameSpeed: number;
  setStats: (stats: CityStats | ((prev: CityStats) => CityStats)) => void;
  setResearch: (research: ResearchState | ((prev: ResearchState) => ResearchState)) => void;
  setGrid: (grid: Grid | ((prev: Grid) => Grid)) => void;
  setMilestones: (milestones: Milestone[] | ((prev: Milestone[]) => Milestone[])) => void;
  setGameStatus: (status: GameStatus) => void;
  setGameStarted: (value: boolean) => void;
  setAiEnabled: (value: boolean) => void;
  setShowLanding: (value: boolean) => void;
  setActiveModal: (modal: ActiveModal) => void;
  setActiveScenario: (scenario: null) => void;
  setGameSpeed: Dispatch<SetStateAction<number>>;
  triggerStory: (customText?: string) => Promise<void>;
  addNewsItem: (item: NewsItem) => void;
  loadScenario: (scenarioId: string, triggerStory: (customText?: string) => Promise<void>) => void;
}

export function useGameSessionBundle({
  newsFeed,
  setNewsFeed,
  currentGoal,
  setCurrentGoal,
  statsDay,
  activeScenario,
  gameStarted,
  gameStatus,
  stats,
  grid,
  research,
  gameSpeed,
  setStats,
  setResearch,
  setGrid,
  setMilestones,
  setGameStatus,
  setGameStarted,
  setAiEnabled,
  setShowLanding,
  setActiveModal,
  setActiveScenario,
  setGameSpeed,
  triggerStory,
  addNewsItem,
  loadScenario,
}: UseGameSessionBundleDeps) {
  const persistence = useGamePersistenceApp({
    newsFeed,
    setNewsFeed,
    currentGoal,
    setCurrentGoal,
    statsDay,
    activeScenario,
    gameStarted,
    gameStatus,
    setStats,
    setResearch,
    setGrid,
    setMilestones,
    setGameStatus,
    setGameStarted,
    setAiEnabled,
    triggerStory,
    addNewsItem,
  });

  useEnhancedObjectivesIntegration({
    gameStarted,
    gameStatus,
    activeScenario,
    stats,
    grid,
    research,
    gameSpeed,
    addNewsItem,
  });

  const handleRetryScenario = useCallback(() => {
    if (!activeScenario) return;
    setActiveModal('none');
    setGameStatus('playing');
    loadScenario(activeScenario.id, triggerStory);
  }, [activeScenario, loadScenario, setActiveModal, triggerStory, setGameStatus]);

  const session = useGameSessionHandlers({
    gameStarted,
    activeScenario,
    setShowLanding,
    setGameStarted,
    setGameStatus,
    setAiEnabled,
    setGrid,
    setStats,
    setResearch,
    setNewsFeed,
    setActiveModal,
    setActiveScenario,
    setGameSpeed,
    saveGame: persistence.saveGame,
    loadGame: persistence.loadGame,
    refreshSavedGameFlag: persistence.refreshSavedGameFlag,
    loadScenario,
    triggerStory,
    addNewsItem,
  });

  return { ...persistence, ...session, handleRetryScenario };
}